import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import CustomCursor from './CustomCursor';
import MusicPlayer from './MusicPlayer';
import PageTransition from './PageTransition';
import ConstellationMenu from './ConstellationMenu';

const Home = () => {
  const navigate = useNavigate();
  const [isPlaying, setIsPlaying] = useState(false);
  const [hoveredHotspot, setHoveredHotspot] = useState(null);
  const [activeHotspot, setActiveHotspot] = useState(null); // For mobile tap
  const [isMobile, setIsMobile] = useState(false);
  const [showShootingStar, setShowShootingStar] = useState(true);
  const [parallaxOffset, setParallaxOffset] = useState({ x: 0, y: 0 });
  const [audioData, setAudioData] = useState(new Array(80).fill(0));
  const [shootingStarPosition, setShootingStarPosition] = useState({
    startX: 10,
    startY: 15,
    endX: 90,
    endY: 35
  });
  const [hotspots] = useState([
    { id: 1, x: 20, y: 15, label: 'music', image: null, disabled: false },
    { id: 2, x: 35, y: 20, label: 'film', image: null, disabled: false },
    { id: 3, x: 50, y: 10, label: 'socials', image: null, disabled: false },
    { id: 4, x: 65, y: 18, label: 'tour', image: null, disabled: true },
    { id: 5, x: 80, y: 22, label: 'merch', image: null, disabled: true },
    { id: 7, x: 10, y: 20, label: 'Shooting Star', image: null, disabled: false }
  ]);
  
  const mountainContainerRef = useRef(null);
  const hoverTimeoutRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);

  // Detect mobile device - only true mobile, not touchscreen laptops
  useEffect(() => {
    const checkMobile = () => {
      // Check for actual mobile devices, not just touch capability
      const isTouchOnly = window.matchMedia('(pointer: coarse)').matches;
      const isSmallScreen = window.innerWidth <= 768;
      setIsMobile(isTouchOnly && isSmallScreen);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close active hotspot when clicking outside (mobile)
  useEffect(() => {
    if (!isMobile) return;
    
    const handleClickOutside = (e) => {
      if (!e.target.closest('.hotspot')) {
        setActiveHotspot(null);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isMobile]);

  // Generate stars with unique IDs
  const [stars] = useState(() => {
    return Array.from({ length: 80 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 50,
      size: Math.random() * 2 + 0.5,
      delay: Math.random() * 3,
      duration: Math.random() * 2 + 2,
      frequencyBand: Math.floor(Math.random() * 8)
    }));
  });

  // Parallax effect on mouse movement (disabled on mobile)
  useEffect(() => {
    if (isMobile) return;
    
    const handleMouseMove = (e) => {
      const centerX = window.innerWidth / 2;
      const centerY = window.innerHeight / 2;
      const offsetX = (e.clientX - centerX) / centerX * 15;
      const offsetY = (e.clientY - centerY) / centerY * 10;
      setParallaxOffset({ x: offsetX, y: offsetY });
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [isMobile]);

  // Audio-reactive stars setup
  useEffect(() => {
    if (!isPlaying) {
      // Reset audio data when not playing
      setAudioData(new Array(80).fill(0));
      return;
    }

    // Try to get audio element from MusicPlayer
    const setupAudioAnalyser = () => {
      const audioElement = document.querySelector('audio');
      if (!audioElement) return;

      try {
        if (!audioContextRef.current) {
          audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }
        
        if (audioContextRef.current.state === 'suspended') {
          audioContextRef.current.resume();
        }

        if (!analyserRef.current) {
          analyserRef.current = audioContextRef.current.createAnalyser();
          analyserRef.current.fftSize = 256;
          
          const source = audioContextRef.current.createMediaElementSource(audioElement);
          source.connect(analyserRef.current);
          analyserRef.current.connect(audioContextRef.current.destination);
        }

        const bufferLength = analyserRef.current.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);

        const updateAudioData = () => {
          analyserRef.current.getByteFrequencyData(dataArray);
          
          // Map frequency data to stars (8 bands)
          const bands = 8;
          const bandSize = Math.floor(bufferLength / bands);
          const newData = [];
          
          for (let i = 0; i < 80; i++) {
            const band = stars[i].frequencyBand;
            let sum = 0;
            for (let j = band * bandSize; j < (band + 1) * bandSize; j++) {
              sum += dataArray[j];
            }
            const average = sum / bandSize;
            newData[i] = average / 255; // Normalize to 0-1
          }
          
          setAudioData(newData);
          animationFrameRef.current = requestAnimationFrame(updateAudioData);
        };

        updateAudioData();
      } catch (e) {
        console.log('Audio analysis not available:', e);
      }
    };

    // Small delay to ensure audio element exists
    const timer = setTimeout(setupAudioAnalyser, 100);
    
    return () => {
      clearTimeout(timer);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isPlaying, stars]);

  // Shooting star control
  useEffect(() => {
    const generateRandomPath = () => {
      const startPositions = [
        { x: Math.random() * 10, y: Math.random() * 10 },
        { x: Math.random() * 10 + 90, y: Math.random() * 10 },
        { x: Math.random() * 10, y: Math.random() * 20 + 15 },
        { x: Math.random() * 10 + 90, y: Math.random() * 20 + 15 },
        { x: Math.random() * 30 + 35, y: Math.random() * 5 }
      ];
      
      const start = startPositions[Math.floor(Math.random() * startPositions.length)];
      let endX = start.x < 50 ? Math.random() * 20 + 75 : Math.random() * 20 + 5;
      let endY = Math.random() * 25 + 15;
      
      return { startX: start.x, startY: start.y, endX, endY };
    };
    
    const initialTimer = setTimeout(() => {
      setShootingStarPosition(generateRandomPath());
      setShowShootingStar(true);
      
      setTimeout(() => setShowShootingStar(false), 25000);
    }, 10000);
    
    const startInterval = () => {
      const intervalDuration = (Math.random() * 60000) + 180000;
      return setInterval(() => {
        setShootingStarPosition(generateRandomPath());
        setShowShootingStar(true);
        setTimeout(() => setShowShootingStar(false), 25000);
      }, intervalDuration);
    };
    
    const intervalTimer = setTimeout(() => {
      const interval = startInterval();
      return () => clearInterval(interval);
    }, 35000);
    
    return () => {
      clearTimeout(initialTimer);
      clearTimeout(intervalTimer);
    };
  }, []);

  // Gravitational lensing effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      const lensables = document.querySelectorAll('.lensable:not(.hotspot)');

      lensables.forEach((element) => {
        const rect = element.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        const distanceX = e.clientX - centerX;
        const distanceY = e.clientY - centerY;
        const distance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);
        const maxDistance = 150;

        if (distance < maxDistance) {
          const strength = 1 - distance / maxDistance;
          const scale = 1 + strength * 0.3;
          const translateX = distanceX / distance * strength * -15;
          const translateY = distanceY / distance * strength * -15;
          element.style.transform = `translate(${translateX}px, ${translateY}px) scale(${scale})`;
          element.style.filter = `blur(${strength * 1}px)`;
        } else {
          element.style.transform = 'translate(0, 0) scale(1)';
          element.style.filter = 'blur(0)';
        }
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const handleHotspotClick = (hotspot, e) => {
    if (hotspot.disabled) return;
    
    // On mobile, first tap shows menu, second tap navigates
    if (isMobile && hotspot.id !== 7) {
      e.stopPropagation();
      if (activeHotspot === hotspot.label) {
        // Second tap - navigate
        navigateToPage(hotspot);
      } else {
        // First tap - show menu
        setActiveHotspot(hotspot.label);
      }
      return;
    }
    
    // Desktop or shooting star - navigate directly
    navigateToPage(hotspot);
  };
  
  const navigateToPage = (hotspot) => {
    if (hotspot.id === 1) navigate('/music');
    else if (hotspot.id === 2) navigate('/film');
    else if (hotspot.id === 3) navigate('/socials');
    else if (hotspot.id === 4) navigate('/tour');
    else if (hotspot.id === 5) navigate('/merch');
    else if (hotspot.id === 7) navigate('/experiments');
  };

  const handleHotspotMouseEnter = (hotspot) => {
    if (hotspot.disabled || isMobile) return;
    clearTimeout(hoverTimeoutRef.current);
    setHoveredHotspot(hotspot.label);
  };

  const handleHotspotMouseLeave = () => {
    if (isMobile) return;
    hoverTimeoutRef.current = setTimeout(() => setHoveredHotspot(null), 300);
  };

  const handleMenuMouseEnter = () => clearTimeout(hoverTimeoutRef.current);
  const handleMenuMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => setHoveredHotspot(null), 300);
  };

  return (
    <>
      <PageTransition>
        <div className="jamblu-container">
          <CustomCursor />
        
          <div className="hero-section">
            {/* Mist Layer */}
            <div className="mist-layer" />
            <div className="mist-layer mist-layer-2" />
            
            {/* Audio-Reactive Stars */}
            <div 
              className="stars-container"
              style={{ transform: `translate(${parallaxOffset.x * 0.5}px, ${parallaxOffset.y * 0.5}px)` }}
            >
              {stars.map((star, index) => {
                const audioIntensity = audioData[index] || 0;
                const baseSize = star.size;
                const reactiveSize = baseSize + audioIntensity * 4;
                const glowIntensity = audioIntensity * 20;
                
                return (
                  <div
                    key={star.id}
                    className="star"
                    style={{
                      left: `${star.x}%`,
                      top: `${star.y}%`,
                      width: `${reactiveSize}px`,
                      height: `${reactiveSize}px`,
                      animationDelay: `${star.delay}s`,
                      animationDuration: `${star.duration}s`,
                      boxShadow: `0 0 ${4 + glowIntensity}px rgba(255, 255, 255, ${0.8 + audioIntensity * 0.2}),
                                  0 0 ${8 + glowIntensity * 2}px rgba(200, 220, 255, ${0.3 + audioIntensity * 0.4})`,
                      transition: 'width 0.1s, height 0.1s, box-shadow 0.1s'
                    }}
                  />
                );
              })}
            </div>
            
            {/* Mountain */}
            <div
              ref={mountainContainerRef}
              className={`mountain-container ${isPlaying ? 'panning' : ''}`}
              style={{ transform: `translate(${parallaxOffset.x * -0.3}px, ${parallaxOffset.y * -0.3}px)` }}
            >
              <img
                src="https://customer-assets.emergentagent.com/job_b4ee3e37-465b-4820-9fda-8b3b42b15eb7/artifacts/0i6wqiwj_mountain.png"
                alt="Jamblu Mountain"
                className="mountain-image lensable"
              />
              
              {/* Hotspots */}
              {hotspots.map((hotspot) => {
                if (hotspot.id === 7 && !showShootingStar) return null;
                
                const isMenuVisible = isMobile 
                  ? activeHotspot === hotspot.label 
                  : hoveredHotspot === hotspot.label;
                
                return (
                  <div
                    key={hotspot.id}
                    className={`hotspot ${hotspot.id === 7 ? 'shooting-star' : 'constellation-hotspot'} ${hotspot.disabled ? 'hotspot-disabled' : ''} ${isMenuVisible ? 'menu-active' : ''}`}
                    data-hotspot={hotspot.id}
                    style={{
                      left: `${hotspot.x}%`,
                      top: `${hotspot.y}%`,
                      ...(hotspot.id === 7 && {
                        '--start-x': `${shootingStarPosition.startX}vw`,
                        '--start-y': `${shootingStarPosition.startY}vh`,
                        '--end-x': `${shootingStarPosition.endX}vw`,
                        '--end-y': `${shootingStarPosition.endY}vh`
                      })
                    }}
                    onClick={(e) => handleHotspotClick(hotspot, e)}
                    onMouseEnter={() => handleHotspotMouseEnter(hotspot)}
                    onMouseLeave={handleHotspotMouseLeave}
                    aria-label={hotspot.label}
                  >
                    {hotspot.id !== 7 && (
                      <>
                        <div className="hotspot-pulse" />
                        <div className="hotspot-core" />
                        <div className="hotspot-label-hover" data-text={hotspot.disabled ? 'coming soon' : hotspot.label}>
                          {hotspot.disabled ? 'coming soon' : hotspot.label}
                        </div>
                        {!hotspot.disabled && (
                          <div onMouseEnter={handleMenuMouseEnter} onMouseLeave={handleMenuMouseLeave}>
                            <ConstellationMenu 
                              hotspotLabel={hotspot.label}
                              isVisible={isMenuVisible}
                              onNavigateToPage={() => navigateToPage(hotspot)}
                            />
                          </div>
                        )}
                      </>
                    )}
                    
                    {hotspot.id === 7 && (
                      <>
                        <div className="hotspot-pulse" />
                        <div className="hotspot-core" />
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            
            {/* Title */}
            <h1 className="jamblu-title lensable">JAMBLU</h1>
          </div>
          
          {/* Music Player - Bottom left */}
          <MusicPlayer onPlayStateChange={setIsPlaying} />
        </div>
      </PageTransition>
    </>
  );
};

export default Home;
